import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Gamepad2, Brain, Timer, Trophy, Sparkles, Loader2, Wine, ArrowRight, RefreshCw, Eye, Check, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { TAG_GROUPS, TAG_COUNT } from "@/lib/expertiseTags";

const GAMES = [
  { kind: "quiz", title: "Quiz éclair", desc: "10 questions, 60 secondes.", icon: Timer },
  { kind: "pairs", title: "Memory cépages", desc: "Associez les paires.", icon: Brain },
  { kind: "order", title: "Ranking", desc: "Classez par latitude, altitude ou superficie.", icon: Gamepad2 },
];

export default function Play() {
  const { user } = useAuth();
  const { t } = useT();
  const [game, setGame] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [topic, setTopic] = useState("Bourgogne");
  const [answers, setAnswers] = useState({});
  const [time, setTime] = useState(60);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState(0);
  const [sponsoredList, setSponsoredList] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [gameReview, setGameReview] = useState(null); // { matched?, userArr? } captured on finish
  const [flashcardBusy, setFlashcardBusy] = useState({});

  const replaySame = () => {
    setAnswers({});
    setTime(60);
    setDone(false);
    setScore(0);
    setShowReview(false);
    setGameReview(null);
  };

  const newGame = () => {
    setGame(null);
    setDone(false);
    setAnswers({});
    setScore(0);
    setShowReview(false);
    setGameReview(null);
  };

  const sendToFlashcards = async (question, correctAnswer, explain, key) => {
    setFlashcardBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post("/microlearning/flashcards/add", {
        question: question,
        answer: correctAnswer,
        explanation: explain || "",
        source: "quiz_review",
      });
      toast.success("Ajoutée à vos flashcards de révision");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ajouter la flashcard");
    } finally {
      setFlashcardBusy((b) => ({ ...b, [key]: false }));
    }
  };
  const [loaderStep, setLoaderStep] = useState(0);

  const LOADER_STEPS = [
    "🍇 geoVINUM AI choisit les bons cépages…",
    "📚 Elle puise dans son atlas du vin…",
    "🧠 Elle rédige des questions piégeuses…",
    "⚖️ Elle équilibre les niveaux de difficulté…",
    "✨ Elle vérifie chaque réponse avec soin…",
    "🎯 Presque prêt, dernière touche…",
  ];

  // Cycle through loader messages every 2.5s
  useEffect(() => {
    if (!busy) { setLoaderStep(0); return; }
    const t = setInterval(() => setLoaderStep((s) => (s + 1) % LOADER_STEPS.length), 2500);
    return () => clearInterval(t);
  }, [busy]); // eslint-disable-line

  useEffect(() => {
    api.get("/games/sponsored").then((r) => setSponsoredList(r.data)).catch(() => {});
    api.get("/games/leaderboard").then((r) => setLeaderboard(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!game || done) return;
    if (time <= 0) {
      // Only auto-finish quiz via parent scoring. Pairs/Order own their score
      // internally and hook via their own time-up effect (see PairsGame/OrderGame).
      if (game.kind === "quiz") finish();
      return;
    }
    const t = setTimeout(() => setTime((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [game, time, done]); // eslint-disable-line

  const start = async (kind) => {
    setBusy(true);
    setBusyKind(kind);
    setGame(null); setDone(false); setAnswers({}); setScore(0); setTime(60);
    try {
      const { data } = await api.post("/games/generate", { kind, topic });
      setGame(data);
    } catch { toast.error("Impossible de générer le jeu"); }
    finally { setBusy(false); setBusyKind(null); }
  };

  const finish = () => {
    if (!game) return;
    const qs = game.questions || [];
    let s = 0;
    qs.forEach((q, i) => { if (answers[i] === q.answer_index) s++; });
    setScore(s); setDone(true);
    if (user) api.post("/games/results", { kind: game.kind, score: s, max: qs.length, duration_s: 60 - time });
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <Toaster theme="light" />
      <span className="chip-pink">Play</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Apprenez en jouant.</h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">Quiz minutés, memory, classements — générés par geoVINUM AI selon vos centres d&apos;intérêt.</p>

      {busy && !game && (
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-[#FCE4ED] via-white to-[#FBF7F3] border-2 border-[#E5276A] p-8 md:p-12 text-center relative overflow-hidden" data-testid="play-loader">
          {/* Animated background blobs */}
          <div className="absolute inset-0 pointer-events-none opacity-60">
            <div className="absolute top-0 left-0 w-32 h-32 bg-[#E5276A]/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#1F3A5F]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          </div>

          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-lg mb-5">
              <img src="/brand/audrey-ai.png" alt="geoVINUM AI" className="w-16 h-16 rounded-full object-cover" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Loader2 className="w-5 h-5 animate-spin text-[#E5276A]" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold">
                geoVINUM AI crée votre {busyKind === "quiz" ? "quiz" : busyKind === "pairs" ? "memory" : "ranking"}
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[#1F3A5F] transition-opacity duration-500" key={loaderStep}>
              {LOADER_STEPS[loaderStep]}
            </h3>
            <p className="text-sm text-[#4A5568] mt-3 max-w-md mx-auto">
              Cela prend généralement 5 à 15 secondes — le temps de personnaliser les questions selon votre niveau et le sujet « <b className="text-[#1F3A5F]">{topic}</b> ».
            </p>
            {/* Progress bar (visual decoration, not synced with backend) */}
            <div className="mt-6 max-w-xs mx-auto h-1.5 rounded-full bg-white/60 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#E5276A] to-[#C01661] rounded-full animate-pulse" style={{ width: `${20 + loaderStep * 13}%`, transition: "width 0.5s ease" }} />
            </div>
          </div>
        </div>
      )}

      {!game && !busy && (
        <>
          <div className="mt-8 max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-[#4A5568] font-bold">Sujet :</span>
              <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="play-topic-input"
                className="flex-1 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] rounded-lg px-3 py-2 text-sm outline-none"
                placeholder="Barolo, Islay, Biodynamie, Rhum agricole…" />
            </div>
            <details className="rounded-xl bg-[#FBF7F3] border border-[#1F3A5F]/8" data-testid="play-topic-suggestions">
              <summary className="cursor-pointer px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-[#E5276A] select-none">
                💡 Suggestions — {TAG_COUNT}+ sujets (régions, cépages, spiritueux, chaîne de valeur)
              </summary>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {TAG_GROUPS.map((g) => (
                  <div key={g.id} data-testid={`play-topic-group-${g.id}`}>
                    <div className="text-[10px] uppercase tracking-wider text-[#8B95A1] font-bold mb-1.5">{g.label}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.tags.map((t) => (
                        <button key={t} onClick={() => setTopic(t)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                            topic === t
                              ? "bg-[#E5276A] text-white border-[#E5276A]"
                              : "bg-white text-[#1F3A5F] border-[#1F3A5F]/12 hover:border-[#E5276A]"
                          }`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {GAMES.map((g) => (
              <button key={g.kind} onClick={() => start(g.kind)} disabled={busy} data-testid={`play-${g.kind}`}
                className="app-card p-6 text-left disabled:opacity-50">
                <div className="w-10 h-10 rounded-lg bg-[#FCE4ED] flex items-center justify-center mb-3"><g.icon className="w-5 h-5 text-[#E5276A]" /></div>
                <div className="font-display text-xl text-[#1F3A5F]">{g.title}</div>
                <div className="text-sm text-[#8B95A1] mt-1">{g.desc}</div>
              </button>
            ))}
          </div>

          {/* geoVINUM AI — Devinez & Déduisez (mini-jeux experts) */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Link to="/audrey" data-testid="play-audrey-card" className="app-card p-6 block hover:-translate-y-0.5 transition-transform relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#FCE4ED] rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <img src="/brand/audrey-ai.png" alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-[#E5276A]/30" />
                  <span className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold">geoVINUM AI · 100 pts</span>
                </div>
                <div className="font-display text-xl text-[#1F3A5F]">Devinez l&apos;appellation</div>
                <div className="text-sm text-[#8B95A1] mt-1">Mode énigme : geoVINUM AI vous décrit un vin, vous devinez l&apos;appellation. 5 tentatives, 3 indices.</div>
              </div>
            </Link>
            <Link to="/deduce" data-testid="play-deduce-card" className="app-card p-6 block hover:-translate-y-0.5 transition-transform relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#FCE4ED] rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <img src="/brand/audrey-ai.png" alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-[#E5276A]/30" />
                  <span className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold">geoVINUM AI · Dégustation</span>
                </div>
                <div className="font-display text-xl text-[#1F3A5F]">Déduisez votre boisson</div>
                <div className="text-sm text-[#8B95A1] mt-1">Mode dégustation guidée. Vin · bière · spiritueux : geoVINUM AI déduit selon vos observations.</div>
              </div>
            </Link>
          </div>

          {sponsoredList.length > 0 && (
            <div className="mt-12">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3">Jeux sponsorisés</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {sponsoredList.map((s) => (
                  <Link key={s.id} to={`/play/sponsored/${s.id}`} className="app-card p-5">
                    <div className="text-xs text-[#8B95A1]">par <strong className="text-[#1F3A5F]">{s.producer_name}</strong></div>
                    <div className="font-display text-lg text-[#1F3A5F] mt-1">{s.title}</div>
                    <div className="text-sm text-[#8B95A1] mt-1">{s.questions?.length || 0} questions · {s.plays} joueurs</div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {leaderboard.length > 0 && (
            <div className="mt-12">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Leaderboard</div>
              <ol className="app-card divide-y divide-[#1F3A5F]/8">
                {leaderboard.slice(0, 10).map((r, i) => (
                  <li key={i} className="flex items-center justify-between px-5 py-3">
                    <span className="text-sm text-[#1F3A5F] font-bold">{i + 1}. {r.user_name || "Anonyme"}</span>
                    <span className="text-xs text-[#8B95A1]">{r.best} pts · {r.plays} parties</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}

      {game && game.kind === "quiz" && !done && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <span className="chip-pink">{game.topic}</span>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1F3A5F] text-white text-sm font-bold"><Timer className="w-4 h-4" /> {time}s</div>
          </div>
          <div className="space-y-4">
            {(game.questions || []).map((q, i) => (
              <div key={i} className="app-card p-5">
                <div className="text-sm font-bold text-[#1F3A5F] mb-3">{i + 1}. {q.q}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {(q.choices || []).map((c, ci) => (
                    <button key={ci} onClick={() => setAnswers((a) => ({ ...a, [i]: ci }))}
                      className={`text-left px-3 py-2 rounded-lg text-sm border transition-colors ${answers[i] === ci ? "bg-[#FCE4ED] border-[#E5276A] text-[#E5276A] font-bold" : "bg-white border-[#1F3A5F]/12 text-[#1F3A5F] hover:bg-[#FBF7F3]"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button onClick={finish} data-testid="play-finish" className="mt-6 btn-primary"><Trophy className="w-4 h-4" /> Terminer</button>
        </div>
      )}

      {game && game.kind === "pairs" && !done && (
        <PairsGame game={game} time={time} onFinish={(s, meta) => { setScore(s); setDone(true); setGameReview(meta || {}); if (user) api.post("/games/results", { kind: "pairs", score: s, max: (game.pairs || []).length, duration_s: 60 - time }); }} />
      )}

      {game && game.kind === "order" && !done && (
        <OrderGame game={game} time={time} onFinish={(s, meta) => { setScore(s); setDone(true); setGameReview(meta || {}); if (user) api.post("/games/results", { kind: "order", score: s, max: (game.items || []).length, duration_s: 60 - time }); }} />
      )}

      {done && game && (
        <div className="mt-8 space-y-4">
          <div className="app-card p-8 text-center" data-testid="play-done">
            <Trophy className="w-10 h-10 text-[#E5276A] mx-auto" />
            <div className="font-display text-4xl text-[#1F3A5F] mt-3">
              {score} / {game.kind === "quiz" ? (game.questions || []).length : game.kind === "pairs" ? (game.pairs || []).length : (game.items || []).length}
            </div>
            <p className="text-[#4A5568] mt-2">Partie terminée en {60 - time}s</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <button onClick={() => setShowReview((v) => !v)} data-testid="play-review-toggle"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
                <Eye className="w-4 h-4" /> {showReview ? "Masquer les réponses" : "Voir les réponses"}
              </button>
              <button onClick={replaySame} data-testid="play-replay-same"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
                <RefreshCw className="w-4 h-4" /> Rejouer ce jeu
              </button>
              <button onClick={newGame} data-testid="play-new-game"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
                <Sparkles className="w-4 h-4" /> Nouveau jeu
              </button>
            </div>
          </div>

          {showReview && game.kind === "quiz" && (
            <div className="space-y-3" data-testid="play-review-list">
              {(game.questions || []).map((q, i) => {
                const userIdx = answers[i];
                const okIdx = q.answer_index;
                const isCorrect = userIdx === okIdx;
                const cardKey = `q_${i}`;
                return (
                  <div key={i} className={`app-card p-5 border-l-4 ${isCorrect ? "border-[#22C55E]" : "border-[#E5276A]"}`}>
                    <div className="flex items-center gap-2 mb-3">
                      {isCorrect
                        ? <Check className="w-4 h-4 text-[#22C55E]" />
                        : <X className="w-4 h-4 text-[#E5276A]" />}
                      <div className="text-sm font-bold text-[#1F3A5F]">{i + 1}. {q.q}</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {(q.choices || []).map((c, ci) => {
                        const isUser = userIdx === ci;
                        const isRight = okIdx === ci;
                        let cls = "bg-white border border-[#1F3A5F]/12 text-[#1F3A5F]";
                        if (isRight) cls = "bg-[#E6F9EE] border border-[#22C55E] text-[#166534] font-bold";
                        else if (isUser && !isRight) cls = "bg-[#FCE4ED] border border-[#E5276A] text-[#C01661] font-bold";
                        return (
                          <div key={ci} className={`px-3 py-2 rounded-lg text-sm ${cls}`}>
                            {isRight && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                            {isUser && !isRight && <X className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                            {c}
                            {isUser && !isRight && <span className="ml-1 text-[10px] uppercase tracking-wider">votre réponse</span>}
                            {isRight && <span className="ml-1 text-[10px] uppercase tracking-wider">bonne réponse</span>}
                          </div>
                        );
                      })}
                    </div>
                    {q.explain && (
                      <p className="mt-3 text-xs text-[#4A5568] italic bg-[#FBF7F3] rounded-lg p-3">
                        <Sparkles className="inline w-3 h-3 text-[#E5276A] mr-1" /> {q.explain}
                      </p>
                    )}
                    {!isCorrect && user && (
                      <button
                        onClick={() => sendToFlashcards(q.q, (q.choices || [])[okIdx] || "", q.explain, cardKey)}
                        disabled={!!flashcardBusy[cardKey]}
                        data-testid={`play-review-flashcard-${i}`}
                        className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#1F3A5F] text-white text-[11px] font-bold hover:bg-[#2A4870] disabled:opacity-60"
                      >
                        {flashcardBusy[cardKey]
                          ? <><Loader2 className="w-3 h-3 animate-spin" /> Ajout…</>
                          : <>📚 Envoyer à ma révision quotidienne</>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {showReview && game.kind === "pairs" && gameReview && (
            <div data-testid="play-review-list">
              <div className="app-card p-5">
                <div className="text-[11px] uppercase tracking-wider text-[#E5276A] font-bold mb-3">
                  Toutes les paires ({(gameReview.pairs || []).length})
                </div>
                <div className="space-y-2">
                  {(gameReview.pairs || []).map((p, i) => {
                    const wasMatched = (gameReview.matched || []).includes(i);
                    return (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${wasMatched ? "bg-[#E6F9EE] border-[#22C55E]" : "bg-[#FCE4ED] border-[#E5276A]"}`}>
                        {wasMatched
                          ? <Check className="w-4 h-4 text-[#22C55E] shrink-0" />
                          : <X className="w-4 h-4 text-[#E5276A] shrink-0" />}
                        <span className="flex-1 text-sm font-bold text-[#1F3A5F]">{p.left}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-[#8B95A1]" />
                        <span className="flex-1 text-sm text-[#1F3A5F]">{p.right}</span>
                        <span className="text-[10px] uppercase tracking-wider text-[#8B95A1]">
                          {wasMatched ? "trouvée" : "manquée"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {showReview && game.kind === "order" && gameReview && (
            <div className="app-card p-5" data-testid="play-review-list">
              <div className="text-[11px] uppercase tracking-wider text-[#E5276A] font-bold mb-3">
                Votre classement vs le bon ordre
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-[#1F3A5F] mb-2">Votre ordre</div>
                  <ol className="space-y-1.5">
                    {(gameReview.userArr || []).map((it, i) => {
                      const isRight = (gameReview.correctArr || [])[i]?.label === it.label;
                      return (
                        <li key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                          isRight ? "bg-[#E6F9EE] border border-[#22C55E] text-[#166534]" : "bg-[#FCE4ED] border border-[#E5276A] text-[#C01661]"
                        }`}>
                          <span className="w-6 h-6 rounded-full bg-white text-[#1F3A5F] font-bold text-xs inline-flex items-center justify-center">{i + 1}</span>
                          <span className="flex-1">{it.label}</span>
                          {isRight ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        </li>
                      );
                    })}
                  </ol>
                </div>
                <div>
                  <div className="text-xs font-bold text-[#1F3A5F] mb-2">Bon ordre</div>
                  <ol className="space-y-1.5">
                    {(gameReview.correctArr || []).map((it, i) => (
                      <li key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#22C55E]/40 text-sm text-[#1F3A5F]">
                        <span className="w-6 h-6 rounded-full bg-[#22C55E] text-white font-bold text-xs inline-flex items-center justify-center">{i + 1}</span>
                        <span className="flex-1">{it.label}</span>
                        {it.value != null && <span className="text-[11px] text-[#8B95A1]">{it.value}</span>}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────── Memory / Pairs game ────────────
function PairsGame({ game, time, onFinish }) {
  const { t } = useT();
  const pairs = game.pairs || [];
  const [cards, setCards] = React.useState(() => {
    const arr = [];
    pairs.forEach((p, i) => {
      arr.push({ id: `${i}-l`, pairIdx: i, label: p.left, side: "left" });
      arr.push({ id: `${i}-r`, pairIdx: i, label: p.right, side: "right" });
    });
    // Shuffle
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  });
  const [flipped, setFlipped] = React.useState([]); // indexes
  const [matched, setMatched] = React.useState(new Set());
  const [moves, setMoves] = React.useState(0);

  // Auto-finish when all pairs matched
  React.useEffect(() => {
    if (matched.size === pairs.length && pairs.length > 0) {
      const t = setTimeout(() => onFinish(matched.size, { matched: Array.from(matched), pairs }), 600);
      return () => clearTimeout(t);
    }
  }, [matched, pairs, onFinish]);

  // Auto-finish when timer hits 0 with current progress (was previously
  // overwritten to 0 by the parent's quiz-only finish path).
  React.useEffect(() => {
    if (time <= 0 && pairs.length > 0 && matched.size < pairs.length) {
      onFinish(matched.size, { matched: Array.from(matched), pairs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  const onClick = (i) => {
    if (matched.has(cards[i].pairIdx)) return;
    if (flipped.includes(i)) return;
    if (flipped.length === 2) return;
    const next = [...flipped, i];
    setFlipped(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      const a = cards[next[0]], b = cards[next[1]];
      if (a.pairIdx === b.pairIdx && a.side !== b.side) {
        setTimeout(() => { setMatched((s) => new Set([...s, a.pairIdx])); setFlipped([]); }, 500);
      } else {
        setTimeout(() => setFlipped([]), 900);
      }
    }
  };

  return (
    <div className="mt-8" data-testid="play-pairs">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="chip-pink">{game.topic}</span>
          <p className="text-xs text-[#8B95A1] mt-1">{t("play.pairsHint").replace("{n}", pairs.length).replace("{m}", moves).replace("{k}", matched.size).replace("{n}", pairs.length)}</p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1F3A5F] text-white text-sm font-bold"><Timer className="w-4 h-4" /> {time}s</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c, i) => {
          const isMatched = matched.has(c.pairIdx);
          const isFlipped = flipped.includes(i) || isMatched;
          return (
            <button key={c.id} onClick={() => onClick(i)} disabled={isMatched}
              data-testid={`play-pairs-card-${i}`}
              className={`aspect-[3/4] rounded-xl border-2 text-center p-3 text-sm font-bold transition-all flex items-center justify-center ${
                isMatched
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700 opacity-70"
                  : isFlipped
                    ? "bg-[#FCE4ED] border-[#E5276A] text-[#1F3A5F]"
                    : "bg-[#1F3A5F] border-[#1F3A5F] text-transparent hover:bg-[#2A4870]"
              }`}>
              {isFlipped ? c.label : "?"}
            </button>
          );
        })}
      </div>
      <button onClick={() => onFinish(matched.size, { matched: Array.from(matched), pairs })} data-testid="play-pairs-finish" className="mt-6 btn-primary">
        <Trophy className="w-4 h-4" /> Terminer
      </button>
    </div>
  );
}

// ──────────── Ranking / Order game ────────────
function OrderGame({ game, time, onFinish }) {
  const { t } = useT();
  const items = game.items || [];
  const [arr, setArr] = React.useState(() => {
    const shuffled = [...items];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });

  const move = (i, dir) => {
    const next = [...arr];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setArr(next);
  };

  const finish = () => {
    const sorted = [...items].sort((a, b) => (a.value || 0) - (b.value || 0));
    let s = 0;
    arr.forEach((it, idx) => { if (sorted[idx]?.label === it.label) s++; });
    onFinish(s, { userArr: arr, correctArr: sorted });
  };

  // Auto-finish when timer hits 0 with the current ordering.
  React.useEffect(() => {
    if (time <= 0 && items.length > 0) finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  return (
    <div className="mt-8" data-testid="play-order">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="chip-pink">{game.topic}</span>
          <p className="text-sm text-[#1F3A5F] mt-2 font-bold">{game.prompt || t("play.rankPrompt")}</p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1F3A5F] text-white text-sm font-bold"><Timer className="w-4 h-4" /> {time}s</div>
      </div>
      <div className="space-y-2">
        {arr.map((it, i) => (
          <div key={i} className="app-card p-4 flex items-center gap-3" data-testid={`play-order-item-${i}`}>
            <span className="w-7 h-7 rounded-full bg-[#FCE4ED] text-[#E5276A] font-bold text-xs inline-flex items-center justify-center shrink-0">{i + 1}</span>
            <span className="flex-1 text-sm text-[#1F3A5F]">{it.label}</span>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0}
                className="p-1 rounded text-[#1F3A5F] hover:bg-[#FBF7F3] disabled:opacity-30">▲</button>
              <button onClick={() => move(i, 1)} disabled={i === arr.length - 1}
                className="p-1 rounded text-[#1F3A5F] hover:bg-[#FBF7F3] disabled:opacity-30">▼</button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={finish} data-testid="play-order-finish" className="mt-6 btn-primary">
        <Trophy className="w-4 h-4" /> Terminer
      </button>
    </div>
  );
}
